
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface CreatorTransaction {
  id: string;
  creator_id: string;
  amount: number;
  created_at: string;
  subscription_type: string;
  status: string;
}

interface CreatorPayout {
  id: string;
  creator_id: string;
  amount: number;
  paid_by: string;
  notes: string;
  status: string;
  created_at: string;
}

interface CreatorEarnings {
  id: string;
  username: string;
  earnings: number;
  subscription_transactions: CreatorTransaction[];
  payouts: CreatorPayout[];
}

export const useCreatorTransactions = () => {
  const [isLoadingState, setIsLoadingState] = useState(true);

  const { data: creators, error } = useQuery<CreatorEarnings[]>({
    queryKey: ["creator-earnings"],
    queryFn: async () => {
      try {
        // Fetch creators with their earnings
        const { data: creators, error: creatorsError } = await supabase
          .from("profiles")
          .select("id, username, earnings")
          .eq("creator_status", "creator")
          .order("earnings", { ascending: false });

        if (creatorsError) throw creatorsError;

        // For each creator, fetch their transactions and payouts
        const creatorsWithData = await Promise.all(
          creators.map(async (creator) => {
            // Fetch transactions
            const { data: transactions, error: transactionsError } = await supabase
              .from("subscription_transactions")
              .select("*")
              .eq("creator_id", creator.id)
              .order("created_at", { ascending: false });

            if (transactionsError) throw transactionsError;

            // Fetch payouts
            const { data: payouts, error: payoutsError } = await supabase
              .from("creator_payouts")
              .select("*")
              .eq("creator_id", creator.id)
              .order("created_at", { ascending: false });

            if (payoutsError) throw payoutsError;

            // Use the earnings directly from the profiles table
            return {
              ...creator,
              subscription_transactions: transactions || [],
              payouts: payouts || []
            };
          })
        );

        return creatorsWithData;
      } catch (error) {
        console.error("Error fetching creator data:", error);
        throw error;
      } finally {
        setIsLoadingState(false);
      }
    },
    meta: {
      onSettled: () => {
        setIsLoadingState(false);
      },
      onError: (error: Error) => {
        toast.error("Failed to load creator earnings");
        console.error("Error loading creator earnings:", error);
      },
    },
  });

  return {
    creators,
    isLoading: isLoadingState,
    error,
  };
};
